/**
 * SlidesRail Component
 * Left sidebar showing draggable list of scene cards
 */

import React, { useState } from 'react';
import { Button, Dropdown, Tooltip, Spin } from 'antd';
import {
  Plus,
  MoreVertical,
  Video,
  Clock,
} from 'lucide-react';
import type { MenuProps } from 'antd';
import { Scene, MultiSceneProject } from '../../../../types/scene';
import { TransitionIndicator } from '../scenes/TransitionIndicator';
import './SlidesRail.scss';

interface SlidesRailProps {
  project: MultiSceneProject | null;
  scenes: Scene[];
  selectedSceneId: string | null;
  isUpdatingScene?: boolean;
  savingTransitionSceneId?: string | null;
  onSceneSelect: (sceneId: string) => void;
  onProjectInfoClick: () => void;
  onProjectTitleChange?: (projectId: string, newTitle: string) => void;
  onAddScene?: () => void;
  onRenameScene?: (sceneId: string) => void;
  onDuplicateScene?: (sceneId: string) => void;
  onDeleteScene?: (sceneId: string) => void;
  onReorderScenes?: (sceneId: string, newIndex: number) => void;
  onSceneTitleChange?: (sceneId: string, newTitle: string) => void;
  onSceneTagsChange?: (sceneId: string, tags: any[]) => void;
  onSceneNotesChange?: (sceneId: string, notes: string) => void;
  onCopySettings?: (sceneId: string) => void;
  onJumpToRecording?: (sceneId: string) => void;
  onEditSettings?: (sceneId: string) => void;
  onExportScene?: (sceneId: string) => void;
  onResetVideo?: (sceneId: string) => void;
  onPreviewScene?: (sceneId: string) => void;
  onConfirmScene?: (sceneId: string) => void;
  onEditTransition?: (fromSceneIndex: number, toSceneIndex: number) => void;
  onPreviewTransition?: (fromSceneIndex: number, toSceneIndex: number) => void;
}

export const SlidesRail: React.FC<SlidesRailProps> = ({
  project,
  scenes,
  selectedSceneId,
  isUpdatingScene = false,
  savingTransitionSceneId,
  onSceneSelect,
  onProjectInfoClick,
  onProjectTitleChange,
  onAddScene,
  onRenameScene,
  onDuplicateScene,
  onDeleteScene,
  onReorderScenes,
  onSceneTitleChange,
  onSceneTagsChange,
  onSceneNotesChange,
  onCopySettings,
  onJumpToRecording,
  onEditSettings,
  onExportScene,
  onResetVideo,
  onPreviewScene,
  onConfirmScene,
  onEditTransition,
  onPreviewTransition,
}) => {
  const [draggedSceneId, setDraggedSceneId] = useState<string | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [dropPosition, setDropPosition] = useState<'before' | 'after'>('before');

  const getSceneStatus = (scene: Scene): { text: string; color: string } => {
    if (scene.status === 'recorded') {
      return { text: 'Recorded', color: 'success' };
    }
    if (scene.status === 'pending_confirmation') {
      return { text: 'Pending Confirmation', color: 'warning' };
    }
    if (scene.isRecording) {
      return { text: 'In progress', color: 'processing' };
    }
    if (scene.recordedAt) {
      return { text: 'Needs review', color: 'warning' };
    }
    return { text: 'Not started', color: 'default' };
  };

  const getCompletionScore = (scene: Scene): number => {
    if (scene.status === 'recorded') return 100;
    if (scene.status === 'pending_confirmation') return 75;
    if (scene.recordedAt) return 50;
    if (scene.isRecording) return 25;
    return 0;
  };

  const formatDuration = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate project completion and total duration
  // Transitions OVERLAP between scenes, so we subtract transition durations from total
  const getProjectProgress = () => {
    const recordedScenes = scenes.filter(s => s.status === 'recorded');
    const completionPercentage = scenes.length > 0 
      ? Math.round((recordedScenes.length / scenes.length) * 100) 
      : 0;
    
    // Calculate total duration: sum of all scene durations MINUS transition overlaps
    // When FFmpeg stitches with crossfade/slide, scenes overlap during transitions
    let totalDurationMs = 0;
    let transitionOverlapMs = 0;
    
    recordedScenes.forEach((scene, index) => {
      // Add full scene duration
      totalDurationMs += scene.duration || 0;
      
      // Subtract transition overlap for all scenes except the last
      // Transition causes overlap between current scene's end and next scene's start
      if (index < recordedScenes.length - 1 && scene.transitionOut) {
        const transitionType = scene.transitionOut.type || 'cut';
        // Only crossfade and slide transitions cause overlap; cut/instant are immediate
        if (transitionType === 'crossfade' || transitionType === 'slide' || transitionType === 'fade') {
          const transitionDurationSec = scene.transitionOut.duration || 0;
          transitionOverlapMs += transitionDurationSec * 1000;
        }
      }
    });
    
    // Final duration accounts for overlapping transitions
    const adjustedDurationMs = Math.max(0, totalDurationMs - transitionOverlapMs);
    const totalSeconds = Math.floor(adjustedDurationMs / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    const durationText = totalSeconds > 0 
      ? `${totalMinutes}:${remainingSeconds.toString().padStart(2, '0')}`
      : '0:00';
    
    return {
      completionPercentage,
      totalDuration: durationText,
      recordedCount: recordedScenes.length,
      totalCount: scenes.length
    };
  };

  const projectProgress = getProjectProgress();

  const getTransitionLabel = (scene: Scene): string => {
    if (!scene.transition) return 'None';
    return scene.transition.type.charAt(0).toUpperCase() + scene.transition.type.slice(1);
  };

  const handleMenuClick = (key: string, sceneId: string) => {
    switch (key) {
      case 'preview':
        onPreviewScene?.(sceneId);
        break;
      case 'confirm-scene':
        onConfirmScene?.(sceneId); // Phase 4.3: Opens RecordingPreviewModal for pending_upload scenes
        break;
      case 'duplicate':
        onDuplicateScene?.(sceneId);
        break;
      case 'copy-settings':
        onCopySettings?.(sceneId);
        break;
      case 'edit-settings':
        onEditSettings?.(sceneId);
        break;
      case 'export':
        onExportScene?.(sceneId);
        break;
      case 'reset-video':
        onResetVideo?.(sceneId);
        break;
      case 'delete':
        onDeleteScene?.(sceneId);
        break;
    }
  };

  const getSceneMenuItems = (scene: Scene): MenuProps['items'] => {
    const items: MenuProps['items'] = [];

    // Add "Confirm Scene" at the top for pending_upload and pending_confirmation scenes with yellow dot
    if (scene.status === 'pending_upload' || scene.status === 'pending_confirmation') {
      items.push({
        key: 'confirm-scene',
        label: (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ 
              width: '6px', 
              height: '6px', 
              borderRadius: '50%', 
              backgroundColor: '#f59e0b',
              display: 'inline-block'
            }} />
            Confirm Scene
          </span>
        ),
      });
      items.push({
        type: 'divider',
      });
    }

    items.push(
      {
        key: 'preview',
        label: 'Preview Scene',
        disabled: !scene.videoUrl && (!scene.videoChunks || scene.videoChunks.length === 0),
      },
      {
        key: 'duplicate',
        label: 'Duplicate Scene',
      },
      {
        key: 'copy-settings',
        label: 'Copy Settings To...',
      },
      {
        type: 'divider',
      },
      {
        key: 'edit-settings',
        label: 'Edit Settings',
      },
      {
        type: 'divider',
      },
      {
        key: 'reset-video',
        label: 'Reset Scene Video',
        disabled: scene.status !== 'recorded' && scene.status !== 'pending_upload' && scene.status !== 'pending_confirmation',
      },
      {
        type: 'divider',
      },
      {
        key: 'delete',
        label: 'Delete Scene',
        danger: true,
      }
    );

    return items;
  };

  const handleDragStart = (e: React.DragEvent, sceneId: string, sourceIndex: number) => {
    const sourceScene = scenes[sourceIndex];
    console.log('🎯 [SlidesRail] ========== DRAG START ==========');
    console.log('🎯 [SlidesRail] Source Scene:', {
      id: sceneId,
      index: sourceIndex,
      title: sourceScene?.title,
      totalScenes: scenes.length,
      allSceneTitles: scenes.map((s, i) => `${i}: ${s.title} (${s.id})`)
    });
    
    // Set drag data
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', sceneId);
    
    // Use setTimeout to set state AFTER the drag has started
    // This prevents re-render from canceling the drag
    setTimeout(() => {
      setDraggedSceneId(sceneId);
    }, 0);
  };

  const handleDragOver = (e: React.DragEvent, index: number, position: 'before' | 'after' = 'before') => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    
    // Log for debugging (only if changed)
    if (dropTargetIndex !== index || dropPosition !== position) {
      console.log('🎯 [SlidesRail] DragOver:', { index, position, targetScene: scenes[index]?.title });
      setDropTargetIndex(index);
      setDropPosition(position);
    }
  };

  const handleDrop = (e: React.DragEvent, visualIndex: number, position: 'before' | 'after' = 'before') => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedSceneId || !onReorderScenes) {
      console.warn('⚠️ [SlidesRail] Drop ignored: missing draggedSceneId or callback');
      return;
    }

    const sourceIndex = scenes.findIndex(s => s.id === draggedSceneId);
    const sourceScene = scenes[sourceIndex];
    const targetScene = scenes[visualIndex];
    
    console.log('🎯 [SlidesRail] ========== DROP EVENT ==========');
    console.log('🎯 [SlidesRail] Drop Details:', {
      draggedSceneId,
      sourceIndex,
      sourceTitle: sourceScene?.title,
      visualIndex,
      targetTitle: targetScene?.title,
      position,
      totalScenes: scenes.length
    });

    // Calculate the correct target index
    // If dropping "before" visualIndex, target is visualIndex
    // If dropping "after" visualIndex, target is visualIndex + 1
    let targetIndex = position === 'before' ? visualIndex : visualIndex + 1;

    // Adjust for the fact that we're removing the source first
    // If source is before target, target index decreases by 1
    if (sourceIndex < targetIndex) {
      targetIndex--;
    }

    console.log('🎯 [SlidesRail] Calculated Target Index:', {
      rawTargetIndex: position === 'before' ? visualIndex : visualIndex + 1,
      adjustedTargetIndex: targetIndex,
      reason: sourceIndex < (position === 'before' ? visualIndex : visualIndex + 1) 
        ? 'Source before target, adjusted down by 1' 
        : 'No adjustment needed'
    });

    if (sourceIndex === targetIndex) {
      console.log('⚠️ [SlidesRail] Same position, skipping reorder');
      setDraggedSceneId(null);
      setDropTargetIndex(null);
      setDropPosition('before');
      return;
    }

    console.log('🎯 [SlidesRail] Calling onReorderScenes:', {
      sceneId: draggedSceneId,
      oldIndex: sourceIndex,
      newIndex: targetIndex,
      expectedResult: `Move "${sourceScene?.title}" from position ${sourceIndex} to ${targetIndex}`
    });
    
    onReorderScenes(draggedSceneId, targetIndex);
    
    setDraggedSceneId(null);
    setDropTargetIndex(null);
    setDropPosition('before');
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.preventDefault();
    console.log('🎯 [SlidesRail] ========== DRAG END ==========');
    setDraggedSceneId(null);
    setDropTargetIndex(null);
    setDropPosition('before');
  };

  return (
    <div className="slides-rail">
      {/* Project Header */}
      <div className="project-header">
        <div className="project-info">
          {/* Scenes Label with Recording Count and Duration */}
          <div className="scenes-header-row">
            <span className="scenes-label-inline">Scenes <span className="recording-count">({projectProgress.recordedCount}/{projectProgress.totalCount} Recorded)</span></span>
            <span className="progress-duration">{projectProgress.totalDuration}</span>
          </div>
          
          {/* Progress Bar */}
          <div className="project-progress">
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill" 
                style={{ width: `${projectProgress.completionPercentage}%` }}
              />
            </div>
          </div>
        </div>

        <Button
          type="primary"
          icon={<Plus size={18} />}
          size="large"
          className="add-scene-button"
          block
          onClick={onAddScene}
        >
          New Scene
        </Button>
      </div>

      {/* Scene List */}
      <div 
        className="scene-list"
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
        }}
      >
        {/* Drop indicator BEFORE the first card */}
        {scenes.length > 0 && dropTargetIndex === 0 && dropPosition === 'before' && (
          <div className="drop-indicator" />
        )}
        
        {/* Invisible drop zone BEFORE the first card for drag detection */}
        {scenes.length > 0 && (
          <div
            className="drop-zone-start"
            onDragOver={(e) => handleDragOver(e, 0, 'before')}
            onDrop={(e) => handleDrop(e, 0, 'before')}
          />
        )}
        
        {scenes.map((scene, index) => {
          const status = getSceneStatus(scene);
          const completion = getCompletionScore(scene);
          const isSelected = scene.id === selectedSceneId;
          const isDragging = scene.id === draggedSceneId;
          const isDropTargetBefore = dropTargetIndex === index && dropPosition === 'before';
          const isDropTargetAfter = dropTargetIndex === index && dropPosition === 'after';

          return (
            <React.Fragment key={scene.id}>
              {/* Drop indicator BEFORE this card (not for first card, handled by drop-zone-start) */}
              {index > 0 && isDropTargetBefore && <div className="drop-indicator" />}
              
              {/* Transition Indicator between scenes (click to edit) */}
              {index > 0 && !draggedSceneId && (
                <div 
                  className="transition-indicator-wrapper"
                >
                  <TransitionIndicator
                    transition={scenes[index - 1]?.transitionOut}
                    onClick={() => onEditTransition?.(index - 1, index)}
                    onPreview={() => onPreviewTransition?.(index - 1, index)}
                    disabled={false}
                    loading={savingTransitionSceneId === scenes[index - 1]?.id}
                  />
                </div>
              )}
              
              <div
                className={`scene-card ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
                draggable={true}
                onDragStart={(e) => {
                  console.log(`🎯 [Card] Drag started for scene ${index}: ${scene.title}`);
                  handleDragStart(e, scene.id, index);
                }}
                onDragOver={(e) => handleDragOver(e, index, 'before')}
                onDrop={(e) => handleDrop(e, index, 'before')}
                onDragEnd={handleDragEnd}
                onClick={() => !isDragging && onSceneSelect(scene.id)}
              >
                {/* Scene Thumbnail with Overlay Controls */}
                <div className="scene-thumbnail">
                  {(scene.thumbnail || scene.thumbnailUrl) ? (
                    <img src={scene.thumbnail || scene.thumbnailUrl} alt={scene.title} />
                  ) : scene.background?.type === 'image' && scene.background?.imageUrl ? (
                    <img src={scene.background.imageUrl} alt={scene.title} style={{ objectFit: 'cover', width: '100%', height: '100%' }} />
                  ) : (
                    <div className="thumbnail-placeholder">
                      <Video size={32} strokeWidth={1.5} />
                    </div>
                  )}
                  
                  {/* Upload Loading Spinner Overlay */}
                  {scene.isUploading && (
                    <div className="upload-overlay">
                      <Spin spinning={true} />
                      <div className="upload-text">Uploading...</div>
                    </div>
                  )}
                  
                  {/* Scene Number Badge */}
                  <div className="scene-number">{index + 1}</div>
                  
                  {/* Duration Badge (show recorded or estimated) */}
                  {scene.duration > 0 && (
                    <div className={`scene-duration ${scene.status !== 'recorded' ? 'estimated' : ''}`}>
                      <Clock size={10} />
                      <span>{scene.status !== 'recorded' ? '~' : ''}{formatDuration(scene.duration)}</span>
                    </div>
                  )}
                  
                  {/* Menu Button (hover only) */}
                  <Dropdown
                    menu={{ 
                      items: getSceneMenuItems(scene),
                      onClick: ({ key }) => handleMenuClick(key, scene.id)
                    }}
                    trigger={['click']}
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<MoreVertical size={14} />}
                      className="scene-menu-button"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Dropdown>
                </div>

                {/* Compact Scene Info */}
                <div className="scene-info-compact">
                  <span className="scene-title-compact">{scene.title}</span>
                  {/* Phase 4.1: Show upload failed badge */}
                  {scene.uploadStatus === 'upload_failed' ? (
                    <Tooltip title="Upload failed - click to retry">
                      <div className="status-badge status-upload-failed">Not uploaded</div>
                    </Tooltip>
                  ) : scene.status === 'pending_upload' ? (
                    <Tooltip title="Recording ready - click 'Confirm Scene' to upload">
                      <div className="status-badge status-pending-upload">Pending</div>
                    </Tooltip>
                  ) : scene.status === 'pending_confirmation' ? (
                    <Tooltip title="Click 'Looks Good' to confirm and save permanently">
                      <div className="status-badge status-pending-confirmation">Pending</div>
                    </Tooltip>
                  ) : status.text === 'Recorded' ? (
                    <Tooltip title="Recording saved to cloud">
                      <div className="status-badge status-recorded">Recorded</div>
                    </Tooltip>
                  ) : scene.isRecording ? (
                    <div className={`status-badge status-recording ${scene.isPaused ? 'paused' : ''}`}>
                      Recording
                      {!scene.isPaused && <span className="recording-dot" />}
                    </div>
                  ) : scene.isUploading ? (
                    <div className="status-badge status-uploading">Saving...</div>
                  ) : (
                    <div className={`status-dot ${status.text.toLowerCase().replace(' ', '-')}`} />
                  )}
                </div>
              </div>
              
              {/* Drop indicator AFTER this card (for last card) */}
              {isDropTargetAfter && <div className="drop-indicator" />}
            </React.Fragment>
          );
        })}
        
        {/* Drop zone AFTER the last card */}
        {scenes.length > 0 && (
          <div
            className="drop-zone-end"
            onDragOver={(e) => handleDragOver(e, scenes.length - 1, 'after')}
            onDrop={(e) => handleDrop(e, scenes.length - 1, 'after')}
          >
            {dropTargetIndex === scenes.length - 1 && dropPosition === 'after' && (
              <div className="drop-indicator" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
